import { GetSingleSubscriptionResponse } from '@api/api/event-alerts';
import { Button, ButtonProps } from '@design/components/button/Button';
import { usePostSubscription } from '@terminal/react-query-hooks/events/use-event-alerts/usePostSubscription';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import {
  ToastPosition,
  useToasts,
} from '@terminal/stores/use-toasts/useToasts';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TrackingToastContent } from '../tracking-toast/TrackingToast';
import {
  getTrackButtonProps,
  TRACKING_TOAST_ID,
  TRACKING_TOAST_SCOPE,
} from '../utils';
import styles from './TrackScenarioInsightButton.module.scss';

export interface TrackScenarioInsightButtonProps {
  insightId: string;
  subscription?: GetSingleSubscriptionResponse;
}

export const TrackScenarioInsightButton = ({
  insightId,
  subscription,
}: TrackScenarioInsightButtonProps) => {
  const { t } = useTranslation('insights');
  const { showToast, clearScopedToasts } = useToasts(state => ({
    showToast: state.showToast,
    clearScopedToasts: state.clearScopedToasts,
  }));

  const { mutate: updateSubscription } = usePostSubscription();
  const { trackAlertTracked, trackAlertUntracked } = usePosthogEvents();

  useEffect(() => {
    return () => clearScopedToasts(TRACKING_TOAST_SCOPE);
  }, []);

  const isTracked = subscription?.tracking_status === 'active';

  const handleTrackClick = () => {
    const nextTracked = !isTracked;
    const trackFn = nextTracked ? trackAlertTracked : trackAlertUntracked;
    trackFn(POSTHOG_TRACKING_SOURCES.SCENARIO_INSIGHT_DRAWER, {
      source_id: insightId,
      source_type: 'scenario-insight',
    });
    updateSubscription(
      {
        source_id: insightId,
        source_type: 'scenario-insight',
        track: nextTracked,
      },
      {
        onSuccess: () => {
          if (nextTracked) {
            showToast({
              id: TRACKING_TOAST_ID,
              scope: TRACKING_TOAST_SCOPE,
              timeout: 5000,
              position: ToastPosition.TopRight,
              content: <TrackingToastContent insightId={insightId} />,
            });
          } else {
            clearScopedToasts(TRACKING_TOAST_SCOPE);
          }
        },
      }
    );
  };

  const buttonProps: ButtonProps = getTrackButtonProps(isTracked, t);

  return (
    <Button
      {...buttonProps}
      className={isTracked ? styles.styledButtonTracked : styles.styledButton}
      onClick={handleTrackClick}
    />
  );
};
